import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { Button, Card, Input, PageHeader, Toast } from '../components/ui/SharedUI'

const ROLE_OPTIONS = [
  { value: 'paid_aide', label: 'I work as a paid caregiver', description: 'Employed by an agency or private client' },
  { value: 'family_caregiver', label: 'I care for a family member', description: 'Paid or unpaid family caregiving' },
  { value: 'care_coordinator', label: 'I coordinate care across multiple caregivers', description: 'Overseeing a loved one\'s total scope of care' },
  { value: 'caregiver_advocate', label: 'I help other caregivers', description: 'Advocate, social worker, or support role' },
]

export function SettingsPage() {
  const { profile, refreshProfile, signOut } = useAuth()
  const [toast, setToast] = useState(null)
  const [saving, setSaving] = useState(false)

  const initialRoles = profile?.roles?.length > 0
    ? profile.roles
    : (profile?.role ? [profile.role] : ['family_caregiver'])

  const [form, setForm] = useState({
    name: profile?.name || '',
    roles: initialRoles,
  })

  function toggleRole(value) {
    setForm(prev => ({
      ...prev,
      roles: prev.roles.includes(value)
        ? prev.roles.filter(r => r !== value)
        : [...prev.roles, value]
    }))
  }

  async function handleSave(e) {
    e.preventDefault()
    if (form.roles.length === 0) {
      setToast({ message: 'Please select at least one role', type: 'error' })
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          name: form.name,
          roles: form.roles,
          // Keep single role in sync with first role for backward compat
          role: form.roles[0],
        })
        .eq('id', profile.id)
      if (error) throw error
      refreshProfile()
      setToast({ message: 'Profile updated', type: 'success' })
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '640px' }}>
      <PageHeader title="Settings" description="Your profile and account preferences." />

      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#1A202C', marginBottom: '1rem' }}>Profile</h3>
        <form onSubmit={handleSave}>
          <Input label="Name" id="name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })} required />

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: '#4A5568', marginBottom: '6px' }}>
              Which of these apply to you?
            </label>
            <p style={{ fontSize: '12px', color: '#718096', marginTop: '-2px', marginBottom: '10px' }}>
              Select all that apply. You can update this anytime as your situation changes.
            </p>
            <div style={{ display: 'grid', gap: '8px' }}>
              {ROLE_OPTIONS.map(option => {
                const checked = form.roles.includes(option.value)
                return (
                  <label key={option.value} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px',
                    border: `1px solid ${checked ? '#2C7A7B' : '#E2E8F0'}`,
                    borderRadius: '8px', cursor: 'pointer',
                    background: checked ? '#E1F5EE' : '#FFFFFF',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleRole(option.value)}
                      style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C', marginBottom: '2px' }}>
                        {option.label}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096', lineHeight: 1.4 }}>
                        {option.description}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          </div>

          <div style={{ fontSize: '13px', color: '#718096', marginBottom: '1rem' }}>
            Email: {profile?.email}
          </div>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </Button>
        </form>
      </Card>

      <Card style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#1A202C', marginBottom: '0.5rem' }}>Account</h3>
        <p style={{ fontSize: '14px', color: '#718096', marginBottom: '1rem' }}>
          Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}
        </p>
        <Button variant="secondary" onClick={signOut}>Sign out</Button>
      </Card>

      <Card>
        <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#1A202C', marginBottom: '0.5rem' }}>Data</h3>
        <p style={{ fontSize: '14px', color: '#718096', marginBottom: '1rem', lineHeight: 1.6 }}>
          Your data belongs to you. Export all your shifts, evidence notes, and other records anytime from the Export page. Every record is stored with row-level security — no other user can access your data.
        </p>
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
